import { BZ2_EDIT_PRODUCT_title_MODIFIED_SUCCESSFULLY } from 'mk/autogenerated/translations/EditProduct.sagas.8d36b5ece8e4c93a1fb42ed4a310541e'
import { bazaar_api_v3_product_detail_url, bazaar_api_v3_product_edit_url } from 'mk/urls/functions';
import { PRODUCT_CREATE_EDIT_FORM_NAME } from 'mk2/apps/bazaar/components/ProductCreateEditForm';
import {
    editProductLoadApi,
    editProductSaveApi,
    EditProductLoadAction,
    EditProductSaveAction,
    EditProductSaveApiNormalizedResponse,
    EDIT_PRODUCT_LOAD,
    EDIT_PRODUCT_SAVE,
} from 'mk2/apps/bazaar/containers/EditProduct/EditProduct.actions';
import { ProductDetailLoadApiNormalizedResponse } from 'mk2/apps/bazaar/containers/ProductDetail/ProductDetail.actions';
import { slugToParams } from 'mk2/apps/bazaar/helpers';
import { MarkEntity, ProductEntity, QualityCounts } from 'mk2/apps/bazaar/schemas';
import { bazaarProductDetailUrl } from 'mk2/apps/bazaar/urls';
import { findComplaintsNonDeleted } from 'mk2/containers/Complaints/Complaints.selectors';
import { handleXHRGetErrorSaga, normalizeError, XHRAction, XHRFormError, XHRGetError } from 'mk2/helpers/api';
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { PhotoUploadStatus } from 'mk2/helpers/form.reducers';
import { url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { entitiesRemove } from 'mk2/reducers/entities';
import { ComplaintSchema, ComplaintTargetType, MarkSchema, ProductSchema } from 'mk2/schemas';
import { findDenormalizedEntitiesByCond, getDenormalizedEntity } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('bazaar/EditProduct.sagas');

interface LoadApiResponse {
    body: {
        correctedFields: string[];
        entities: ProductDetailLoadApiNormalizedResponse['entities'];
        result: ProductDetailLoadApiNormalizedResponse['result'];
        qualityCounts: QualityCounts;
    };
}

function* load({ xhr, slug }: EditProductLoadAction & XHRAction) {
    yield put(editProductLoadApi.request(slug));

    try {
        const response: LoadApiResponse = yield call(() => xhr.get(bazaar_api_v3_product_detail_url(slug)));

        yield put(
            editProductLoadApi.success(slug, response.body.correctedFields, response.body.qualityCounts, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(editProductLoadApi.failure(slug, normalizeError(error)));
    }
}

interface SaveApiResponse {
    body: {
        entities: EditProductSaveApiNormalizedResponse['entities'];
        result: EditProductSaveApiNormalizedResponse['result'];
    };
}

function* save({ productId, data, xhr }: EditProductSaveAction & XHRAction) {
    yield put(editProductSaveApi.request(productId, data));

    try {
        let price = data.price.replace(/,/g, '.').replace(/ /g, '');
        if (price.endsWith('.')) {
            // Convert 1234. to 1234.0
            price += '0';
        }

        let eshopUrl = data.eshopUrl;
        if (eshopUrl && !/^https?:\/\//.test(eshopUrl)) {
            // Add http:// as default
            eshopUrl = `http://${eshopUrl}`;
        }

        const description = data.description.replace(/\n\n\n+/g, '\n\n');

        const response: SaveApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_product_edit_url(productId), {
                ...data,
                photos: data.photos ? data.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS) : [],
                // Convert 1234. to 1234.0
                price,
                eshopUrl,
                description,
            }),
        );

        const product = getDenormalizedEntity<ProductEntity>(
            response.body,
            ProductSchema,
            response.body.result.product,
        );

        yield handleXHRFormSuccessSaga(
            PRODUCT_CREATE_EDIT_FORM_NAME,
            url(bazaarProductDetailUrl, slugToParams(product.slug)),
            interpolate(BZ2_EDIT_PRODUCT_title_MODIFIED_SUCCESSFULLY, { title: product.title }),
        );
        yield put(
            editProductSaveApi.success(productId, data, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );

        // Remove complaints and marks from entities cache
        const marks = yield select((state) =>
            findDenormalizedEntitiesByCond<MarkEntity>(
                state,
                MarkSchema,
                `cacheKey-EditProduct-sagas-${productId}-marks`,
                (sel) => sel.targetId === productId,
            ),
        );
        const complaints = yield select((state) =>
            findComplaintsNonDeleted(state, ComplaintTargetType.BazaarProduct, productId),
        );

        if (marks?.length || complaints?.length) {
            yield put(
                entitiesRemove({
                    [MarkSchema.key]: marks ? marks.map((m) => m.id) : [],
                    [ComplaintSchema.key]: complaints ? complaints.map((c) => c.id) : [],
                }),
            );
        }
    } catch (error) {
        yield handleXHRFormErrorSaga(PRODUCT_CREATE_EDIT_FORM_NAME, error as XHRFormError, logger);
        yield put(editProductSaveApi.failure(productId, data, normalizeError(error)));
    }
}

export default function* root() {
    yield all([takeLatest(EDIT_PRODUCT_LOAD, load), takeEvery(EDIT_PRODUCT_SAVE, save)]);
}
